import { useAiProvider } from '../../lib/ai-provider';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../../lib/auth';
import { isModelSelection, type ChatMessage, type HomeContextSnapshot, type ModelSelection, type PageContext } from '../../lib/api-types';
import { GoalSnapshot } from '../content/GoalEditor';
import type { StreamState } from '../../lib/stream-accumulator';
import { conversationQuery } from '../../lib/queries';
import { stageImport } from '../../lib/import-upload';
import { Composer } from './Composer';
import { MessageView, PartsView, ThinkingIndicator } from './MessageList';
import { useChatStream } from './useChatStream';
import { shuffledSetupNudges } from './setup-nudges';
import styles from './Chat.module.css';

const landingPromptByConversation = new Map<string, number>();
let hasShownInitialLandingPrompt = false;

function landingPromptIndex(conversationId: string): number {
  const existing = landingPromptByConversation.get(conversationId);
  if (existing !== undefined) return existing;

  const index = hasShownInitialLandingPrompt ? Math.floor(Math.random() * 3) : 0;
  hasShownInitialLandingPrompt = true;
  landingPromptByConversation.set(conversationId, index);
  return index;
}

export function ChatView({
  conversationId,
  onConversationSettled,
  pageContext,
  intro,
  placeholder,
  onConversationCreated,
  onRefresh,
  onAutoProgressRefresh,
  autoProgressStatus,
  autoProgressSetup = false,
  composerInIntro = false,
}: {
  conversationId: string | null;
  onConversationSettled?: (conversationId: string) => void;
  pageContext?: PageContext;
  intro?: ReactNode | ((controls: { autoProgress: () => void; running: boolean; composer: ReactNode; setupNudge: string | null; sendMessage: (text: string) => void; isEmpty: boolean }) => ReactNode);
  onRefresh?: (() => Promise<void>) | undefined;
  onAutoProgressRefresh?: () => Promise<void>;
  autoProgressStatus?: ReactNode;
  autoProgressSetup?: boolean;
  composerInIntro?: boolean;
  placeholder?: string;
  onConversationCreated?: (conversationId: string) => void;
}) {
  const conversation = useQuery({
    ...conversationQuery(conversationId ?? ''),
    enabled: Boolean(conversationId),
  });
  const [modelOverride, setModelOverride] = useState<{
    conversationId: string | null;
    model: ModelSelection;
  } | null>(null);
  // The API owns the durable conversation choice. Keep a conversation-scoped
  // override so an in-flight refetch cannot replace a newly selected model.
  const aiProvider = useAiProvider();
  const activeProvider = aiProvider.data?.enabled ? aiProvider.data.provider ?? 'anthropic' : 'anthropic';
  const persistedModel = conversation.data?.conversation.model;
  const candidateModel = modelOverride?.conversationId === conversationId
    ? modelOverride.model
    : isModelSelection(persistedModel) ? persistedModel : 'auto';
  const model = candidateModel === 'auto' || candidateModel.startsWith('gpt-') === (activeProvider === 'openai') ? candidateModel : 'auto';
  const { send, ensureConversation, cancel, cancelPersistedTurn, pendingUserMessage, stream, isStreaming } = useChatStream(
    conversationId,
    onConversationSettled,
    pageContext,
    onConversationCreated,
  );

  const messages = conversation.data?.conversation.messages ?? [];
  const [initialHomeContext, setInitialHomeContext] = useState<HomeContextSnapshot | null>(null);
  const isHome = pageContext?.pageSpecificContext?.surface === 'home';
  const homeContext = conversation.data?.conversation.homeContext ?? initialHomeContext;
  const captureHomeContext = () => {
    if (isHome && !initialHomeContext && messages.length === 0) {
      const context = pageContext?.pageSpecificContext;
      setInitialHomeContext({
        goals: typeof context?.goals === 'string' ? context.goals : '',
        currentMission: typeof context?.currentMission === 'string' ? context.currentMission : null,
        nextStepHint: typeof context?.nextStep === 'string' ? context.nextStep : null,
        capturedAt: new Date().toISOString(),
      });
    }
  };
  const sendFromSurface = (text: string, mode?: 'progress') => {
    captureHomeContext();
    return mode ? send(text, model, mode) : send(text, model);
  };
  const persistedStreamingMessage = messages.find(
    (message) => message.role === 'assistant' && message.status === 'streaming',
  );
  const turnIsRunning = isStreaming || Boolean(persistedStreamingMessage);
  const [showSync, setShowSync] = useState(false);
  const progressLock = useRef(false);

  useEffect(() => {
    if (!turnIsRunning) return;
    const stopOnEscape = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.repeat) return;
      event.preventDefault();
      if (persistedStreamingMessage) void cancelPersistedTurn(persistedStreamingMessage.id);
      else cancel();
    };
    window.addEventListener('keydown', stopOnEscape);
    return () => window.removeEventListener('keydown', stopOnEscape);
  }, [cancel, cancelPersistedTurn, persistedStreamingMessage, turnIsRunning]);

  const [setupNudges, setSetupNudges] = useState<string[] | null>(null);
  const setupNudge = setupNudges?.[0] ?? null;
  useEffect(() => { setSetupNudges(null); }, [conversationId, autoProgressSetup]);
  const pageRef = useRef<HTMLDivElement>(null);
  const autoProgress = async () => {
    if (turnIsRunning || progressLock.current) return;
    if (autoProgressSetup) {
      setSetupNudges(shuffledSetupNudges(setupNudge));
      pageRef.current?.querySelector<HTMLTextAreaElement>('textarea[aria-label="Message"]')?.focus();
      return;
    }
    progressLock.current = true;
    setShowSync(Boolean(onAutoProgressRefresh));
    // Sync is optional context, never a prerequisite for starting this turn.
    // The refresh owner displays provider status and coalesces ongoing jobs.
    void Promise.resolve().then(() => onAutoProgressRefresh?.()).catch(() => {});
    try {
      await sendFromSurface('Auto Progress', 'progress');
    } finally {
      progressLock.current = false;
    }
  };
  const isEmpty = messages.length === 0 && !pendingUserMessage && !stream;
  const inlineComposer = composerInIntro && isEmpty && typeof intro === 'function' && (!conversationId || conversation.isSuccess);
  useEffect(() => {
    if (inlineComposer) {
      pageRef.current?.querySelector<HTMLTextAreaElement>('textarea[aria-label="Message"]')?.focus({ preventScroll: true });
    }
  }, [inlineComposer]);
  const composer = (
    <Composer
        provider={activeProvider}
      onSend={(text) => { setSetupNudges(null); setShowSync(false); void sendFromSurface(text); }}
      onStop={() => {
        if (persistedStreamingMessage) void cancelPersistedTurn(persistedStreamingMessage.id);
        else cancel();
      }}
      onStageImport={async (file) => stageImport(await ensureConversation(), file)}
      isStreaming={turnIsRunning}
      model={model}
      onModelChange={(nextModel) => setModelOverride({ conversationId, model: nextModel })}
      placeholder={placeholder ?? 'Message Resonance…'}
    />
  );
  const introContent = typeof intro === 'function' ? intro({ autoProgress: () => void autoProgress(), running: turnIsRunning,
    composer: inlineComposer ? <div className={styles.inlineComposer}>{composer}</div> : null, setupNudge,
    sendMessage: (text) => { if (!turnIsRunning) { setShowSync(false); void sendFromSurface(text); } }, isEmpty }) : intro;
  const threadIntro = <>
    {homeContext && <GoalSnapshot snapshot={homeContext} />}
    {(!isHome || isEmpty) && introContent}
  </>;

  return (
    <div className={styles.page} ref={pageRef}>
      {isEmpty && !intro ? (
        <div className={styles.emptyState}>
          <Landing conversationId={conversationId ?? 'draft'} />
          {composer}
        </div>
      ) : (
        <>
          <Thread
            messages={messages}
            pendingUserMessage={pendingUserMessage}
            stream={stream}
            isStreaming={isStreaming}
            intro={threadIntro}
            prelude={showSync ? autoProgressStatus : null}
            onRefresh={onRefresh}
          />
          {conversation.isError && <p role="alert">Couldn’t load this conversation. <button onClick={() => void conversation.refetch()}>Try again</button></p>}
          {!inlineComposer && composer}
        </>
      )}
    </div>
  );
}

function Landing({ conversationId }: { conversationId: string }) {
  const { bootstrap } = useAuth();
  const firstName = bootstrap?.user?.firstName;
  const prompts = [
    'What are we working on?',
    firstName ? `Good to see you, ${firstName}.` : 'Good to see you.',
    'What should we move forward today?',
  ];
  const prompt = prompts[landingPromptIndex(conversationId)] ?? prompts[0];

  return (
    <div className={styles.landing}>
      <div className={styles.landingInner}>
        <h1 className={styles.greeting}>{prompt}</h1>
      </div>
    </div>
  );
}

/**
 * Whether the in-flight turn has rendered anything the reader can see. Empty
 * text parts count as nothing: a text part is created as soon as the first
 * delta arrives. Email tool results also render live review cards.
 */
function hasVisibleOutput(stream: StreamState): boolean {
  return stream.parts.some((part) => {
    if (part.type === 'text') return Boolean(part.text?.trim());
    if (part.type === 'thinking') return Boolean(part.text?.trim());
    if (part.type === 'citation') return Boolean(part.citation?.url);
    if (part.type === 'tool_call') return true;
    if (part.type === 'tool_result') return Boolean(part.error ||
      (part.output && typeof part.output === 'object' && ('emailCard' in part.output || 'artifactCard' in part.output)));
    return false;
  });
}

function Thread({
  messages,
  pendingUserMessage,
  stream,
  isStreaming,
  intro,
  prelude,
  onRefresh,
}: {
  messages: ChatMessage[];
  pendingUserMessage: ChatMessage | null;
  stream: StreamState | null;
  isStreaming: boolean;
  intro?: ReactNode;
  prelude?: ReactNode;
  onRefresh?: (() => Promise<void>) | undefined;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const pinnedToBottom = useRef(true);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    const element = scroller.current;
    if (!element || !onRefresh) return;
    let start: { x: number; y: number } | undefined;
    let amount = 0;
    let busy = false;
    const reset = () => { start = undefined; amount = 0; setPull(0); };
    const begin = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch || event.touches.length !== 1 || busy || element.scrollTop > 0 ||
        (event.target as HTMLElement).closest('input,textarea,button,a,select,summary,[contenteditable]')) return;
      start = { x: touch.clientX, y: touch.clientY };
    };
    const move = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!start || !touch || event.touches.length !== 1) { reset(); return; }
      const dy = touch.clientY - start.y, dx = touch.clientX - start.x;
      if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || element.scrollTop > 0) { reset(); return; }
      if (dy < 12) return;
      if (event.cancelable) event.preventDefault();
      amount = Math.min(84, dy * .45);
      setPull(amount);
    };
    const finish = () => {
      const shouldRefresh = amount >= 58;
      reset();
      if (!shouldRefresh || busy) return;
      busy = true; setRefreshing(true);
      void onRefresh().catch(() => {}).finally(() => { busy = false; setRefreshing(false); });
    };
    element.addEventListener('touchstart', begin, { passive: true });
    element.addEventListener('touchmove', move, { passive: false });
    element.addEventListener('touchend', finish);
    element.addEventListener('touchcancel', reset);
    return () => {
      element.removeEventListener('touchstart', begin); element.removeEventListener('touchmove', move);
      element.removeEventListener('touchend', finish); element.removeEventListener('touchcancel', reset);
    };
  }, [onRefresh]);

  // Track whether the reader is at the bottom, so an incoming token does not
  // yank them away from something they scrolled up to read.
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const onScroll = (): void => {
      const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
      pinnedToBottom.current = distance < 80;
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => element.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    if (intro && !messages.length && !pendingUserMessage && !stream && !prelude) return;
    if (!pinnedToBottom.current) return;
    const element = scroller.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [messages, pendingUserMessage, stream, prelude]);

  return (
    <div className={styles.thread} ref={scroller} data-refreshable={!!onRefresh}>
      {onRefresh && <div className={styles.pullRefresh} style={{ height: refreshing ? 44 : pull }} role="status">
        {(pull > 0 || refreshing) && <span>{refreshing ? 'Refreshing…' : pull >= 58 ? 'Release to refresh' : 'Pull to refresh'}</span>}
      </div>}
      <div className={styles.threadInner}>
        {intro}
        {messages.map((message) => (
          <MessageView key={message.id} message={message} />
        ))}
        {pendingUserMessage ? <MessageView message={pendingUserMessage} /> : null}
        {prelude}
        {stream ? (
          <div className={styles.message} data-role="assistant">
            <PartsView parts={stream.parts} streaming={isStreaming} />
            {/*
              Nothing visible yet means the model is still thinking. Tool calls
              render their own chip, so this only fills the genuinely blank gap
              — before the first token, and between a tool result and the reply.
            */}
            {isStreaming && !hasVisibleOutput(stream) ? <ThinkingIndicator /> : null}
            {stream.status === 'cancelled' ? <div className={styles.stopped}>Stopped</div> : null}
            {stream.error ? <div className={styles.error}>{stream.error}</div> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
